import { useEffect, useState } from 'react';
import { packageService, Package } from '../../services/packages';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';

export default function Packages() {
    const [packages, setPackages] = useState<Package[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPackage, setEditingPackage] = useState<Package | null>(null);

    // Form State
    const [formData, setFormData] = useState<Partial<Package>>({
        title: '',
        description: '',
        price: 0,
        type: 'hajj',
        duration: '',
        dates: '',
        features: [],
        image_url: '',
        is_active: true
    });
    const [featuresInput, setFeaturesInput] = useState('');

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const data = await packageService.getAllPackagesAdmin();
            setPackages(data);
        } catch (error) {
            console.error('Error fetching packages:', error);
            alert('Failed to fetch packages');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (pkg?: Package) => {
        if (pkg) {
            setEditingPackage(pkg);
            setFormData(pkg);
            setFeaturesInput(pkg.features ? pkg.features.join(', ') : '');
        } else {
            setEditingPackage(null);
            setFormData({
                title: '',
                description: '',
                price: 0,
                type: 'hajj',
                duration: '',
                dates: '',
                features: [],
                image_url: '',
                is_active: true
            });
            setFeaturesInput('');
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPackage(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const featuresArray = featuresInput.split(',').map(f => f.trim()).filter(f => f);
            const dataToSubmit = { ...formData, features: featuresArray } as any;

            if (editingPackage) {
                await packageService.updatePackage(editingPackage.id, dataToSubmit);
            } else {
                await packageService.createPackage(dataToSubmit);
            }
            fetchPackages();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving package:', error);
            alert('Failed to save package');
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            try {
                await packageService.deletePackage(id);
                fetchPackages();
            } catch (error) {
                console.error('Error deleting package:', error);
                alert('Failed to delete package');
            }
        }
    };

    if (loading) return <div className="p-8 text-center text-white">Loading packages...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Packages Management</h1>
                <Button onClick={() => handleOpenModal()}>
                    <i className="ri-add-line mr-2"></i>
                    Add Package
                </Button>
            </div>

            <div className="bg-gray-900 rounded-lg shadow overflow-hidden border border-gray-800">
                <table className="min-w-full divide-y divide-gray-800">
                    <thead className="bg-gray-800">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-gray-900 divide-y divide-gray-800">
                        {packages.map((pkg) => (
                            <tr key={pkg.id} className="hover:bg-gray-800">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-white">{pkg.title}</div>
                                    <div className="text-xs text-gray-500">{pkg.duration}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${pkg.type === 'hajj' ? 'bg-purple-900/50 text-purple-200' : 'bg-blue-900/50 text-blue-200'}`}>
                                        {pkg.type.toUpperCase()}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                    ₦{pkg.price.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${pkg.is_active ? 'bg-green-900/50 text-green-200' : 'bg-red-900/50 text-red-200'}`}>
                                        {pkg.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                                    <button
                                        onClick={() => handleOpenModal(pkg)}
                                        className="text-blue-400 hover:text-blue-300"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(pkg.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700">
                            <form onSubmit={handleSubmit}>
                                <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg leading-6 font-medium text-white mb-4">
                                        {editingPackage ? 'Edit Package' : 'Add New Package'}
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300">Title</label>
                                            <Input
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                required
                                                className="bg-gray-700 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Type</label>
                                                <select
                                                    value={formData.type}
                                                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'hajj' | 'umrah' })}
                                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                                >
                                                    <option value="hajj">Hajj</option>
                                                    <option value="umrah">Umrah</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Price (₦)</label>
                                                <Input
                                                    type="number"
                                                    value={formData.price}
                                                    onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                                                    required
                                                    className="bg-gray-700 border-gray-600 text-white"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300">Description</label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-600 bg-gray-700 text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                rows={3}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Duration</label>
                                                <Input
                                                    value={formData.duration}
                                                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                                    placeholder="e.g. 14 Days"
                                                    className="bg-gray-700 border-gray-600 text-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Dates</label>
                                                <Input
                                                    value={formData.dates}
                                                    onChange={(e) => setFormData({ ...formData, dates: e.target.value })}
                                                    placeholder="e.g. June 10 - 24"
                                                    className="bg-gray-700 border-gray-600 text-white"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300">Features (comma separated)</label>
                                            <Input
                                                value={featuresInput}
                                                onChange={(e) => setFeaturesInput(e.target.value)}
                                                placeholder="Hotel, Visa, Transport..."
                                                className="bg-gray-700 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300">Image URL</label>
                                            <Input
                                                value={formData.image_url}
                                                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                                className="bg-gray-700 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label className="ml-2 block text-sm text-gray-300">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <Button type="submit" className="w-full sm:w-auto sm:ml-3">
                                        Save
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-800 text-base font-medium text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
